import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, formatBytes, inboxApi, type CannedReply } from '@wa/shared';

/**
 * Quick replies, managed as a list.
 *
 * Saving one still works from the composer — type it, press `/`, give it a
 * shortcut — because that is where the third repetition of a sentence happens.
 * This screen is for the other half of the job: seeing what the team already has,
 * fixing wording, retiring the ones nobody uses, and the two things the composer
 * cannot do: attach a file, and write placeholders on purpose.
 *
 * Placeholders: {{name}}, {{first_name}}, {{agent}}, {{field.key}} are filled
 * from the chat; anything else ({{order_no}}) is left for the agent to type,
 * selected in the box, and the send is refused until it is. That is how a quick
 * reply becomes a small template without any of Meta's review.
 *
 * A file is uploaded here on one of the account's numbers and cloned by the
 * server to whichever number a chat is on, the first time it is used there.
 */
export function QuickRepliesPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [shortcut, setShortcut] = useState('');
  const [body, setBody] = useState('');
  const [file, setFile] = useState<Attached | null>(null);

  const replies = useQuery({ queryKey: ['canned-replies'], queryFn: inboxApi.cannedReplies });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });
  const uploadOn = (channels.data ?? []).find((c) => c.isActive)?.id ?? null;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const create = useMutation({
    mutationFn: () => inboxApi.createCannedReply(shortcut.trim().replace(/^\//, ''), body.trim(), file?.assetId ?? null),
    onSuccess: () => { setError(null); setShortcut(''); setBody(''); setFile(null); invalidate(); },
    onError: (e) => fail(e, 'Could not save the quick reply.'),
  });

  const update = useMutation({
    mutationFn: (draft: Draft) => inboxApi.updateCannedReply(
      draft.id, draft.shortcut.trim().replace(/^\//, ''), draft.body.trim(),
      // null = unchanged, 0 = remove, otherwise the new upload.
      draft.file === undefined ? null : draft.file === null ? 0 : draft.file.assetId,
    ),
    onSuccess: () => { setError(null); setEditing(null); invalidate(); },
    onError: (e) => fail(e, 'Could not save the change.'),
  });

  const remove = useMutation({
    mutationFn: inboxApi.deleteCannedReply,
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e) => fail(e, 'Could not delete it.'),
  });

  const busy = create.isPending || update.isPending || remove.isPending;
  const canAdd = shortcut.trim().replace(/^\//, '') !== '' && body.trim() !== '';

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          New quick reply
        </h2>
        <form
          className="flex flex-col gap-3 px-3 py-3 sm:px-4"
          onSubmit={(e) => { e.preventDefault(); if (canAdd && !busy) create.mutate(); }}
        >
          <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
            <label className="block min-w-0">
              <span className="mb-1 block text-micro font-medium uppercase tracking-wide text-(--color-faint)">Shortcut</span>
              <span className="flex items-center rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 focus-within:border-(--color-brand)">
                <span className="text-small text-(--color-muted)">/</span>
                <input
                  value={shortcut}
                  onChange={(e) => setShortcut(e.target.value.replace(/\s+/g, '-'))}
                  placeholder="greeting"
                  maxLength={60}
                  className="min-w-0 flex-1 bg-transparent py-2 text-small outline-none"
                />
              </span>
              <span className="mt-1 block text-micro text-(--color-muted)">Letters, numbers, dashes — what an agent types after the slash.</span>
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-micro font-medium uppercase tracking-wide text-(--color-faint)">Text</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder={'Hello {{first_name}}, {{agent}} here from PSM. Your order {{order_no}} is on its way.'}
                className="w-full resize-y rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)"
              />
              <Placeholders />
            </label>
          </div>

          <AttachmentField file={file} onChange={setFile} channelId={uploadOn} onError={setError} />

          <div>
            <button
              type="submit"
              disabled={!canAdd || busy}
              className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
            >
              {create.isPending ? 'Saving…' : 'Add quick reply'}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          {replies.data ? `${replies.data.length} quick ${replies.data.length === 1 ? 'reply' : 'replies'}` : 'Quick replies'}
        </h2>

        {replies.isPending && <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">Loading…</p>}
        {replies.isError && <p className="px-3 py-3 text-small text-(--color-bad) sm:px-4">Could not load quick replies.</p>}
        {replies.data?.length === 0 && (
          <p className="px-3 py-3 text-small text-(--color-muted) sm:px-4">
            None yet. Agents will see them by typing <code>/</code> as the first character of a reply.
          </p>
        )}

        <ul className="divide-y divide-(--color-line)">
          {replies.data?.map((reply) => (
            <li key={reply.id} className="px-3 py-2.5 sm:px-4">
              {editing?.id === reply.id ? (
                <form
                  className="flex flex-col gap-2"
                  onSubmit={(e) => { e.preventDefault(); if (!busy) update.mutate(editing); }}
                >
                  <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
                    <span className="flex items-center rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 focus-within:border-(--color-brand)">
                      <span className="text-small text-(--color-muted)">/</span>
                      <input
                        value={editing.shortcut}
                        onChange={(e) => setEditing({ ...editing, shortcut: e.target.value.replace(/\s+/g, '-') })}
                        maxLength={60}
                        aria-label="Shortcut"
                        className="min-w-0 flex-1 bg-transparent py-1.5 text-small outline-none"
                      />
                    </span>
                    <textarea
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      rows={3}
                      maxLength={2000}
                      aria-label="Text"
                      className="w-full resize-y rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-1.5 text-small outline-none focus:border-(--color-brand)"
                    />
                  </div>
                  <AttachmentField
                    file={editing.file === undefined ? existingFile(reply) : editing.file}
                    onChange={(next) => setEditing({ ...editing, file: next })}
                    channelId={uploadOn}
                    onError={setError}
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={busy || editing.shortcut.trim() === '' || editing.body.trim() === ''}
                      className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
                    >
                      {update.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
                  <div className="min-w-0 flex-1 basis-56">
                    <p className="font-mono text-small font-medium">/{reply.shortcut}</p>
                    <p className="whitespace-pre-wrap text-small text-(--color-ink-soft)">{reply.body}</p>
                    {reply.mediaAssetId && (
                      <p className="mt-0.5 text-micro text-(--color-muted)">File: {reply.mediaFileName ?? 'attached'}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => { setError(null); setEditing({ id: reply.id, shortcut: reply.shortcut, body: reply.body, file: undefined }); }}
                      className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => remove.mutate(reply.id)}
                      className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad) disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

interface Attached { assetId: number; name: string; size: number | null }

/** The edit form's draft. `file` undefined = untouched; null = removed; set = replaced. */
interface Draft { id: number; shortcut: string; body: string; file: Attached | null | undefined }

function existingFile(reply: CannedReply): Attached | null {
  return reply.mediaAssetId ? { assetId: reply.mediaAssetId, name: reply.mediaFileName ?? 'attached', size: null } : null;
}

function Placeholders() {
  return (
    <span className="mt-1 block text-micro text-(--color-muted)">
      Filled from the chat: <code>{'{{name}}'}</code>, <code>{'{{first_name}}'}</code>, <code>{'{{agent}}'}</code>,{' '}
      <code>{'{{field.key}}'}</code> for a custom field. Any other <code>{'{{word}}'}</code> is left for the agent to type
      before sending. Up to 2,000 characters.
    </span>
  );
}

/**
 * One file for the quick reply. Uploaded the moment it is chosen — to WhatsApp,
 * on the first active number — so the save only carries an id; the server clones
 * it to other numbers when a chat there first uses it.
 */
function AttachmentField({ file, onChange, channelId, onError }: {
  file: Attached | null;
  onChange: (next: Attached | null) => void;
  channelId: number | null;
  onError: (message: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (picked: File) => inboxApi.uploadHeaderMedia(channelId!, picked),
    onSuccess: (uploaded, picked) => { onError(null); onChange({ assetId: uploaded.mediaAssetId, name: uploaded.fileName, size: picked.size }); },
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not upload the file.'),
  });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2">
      <input
        ref={input}
        type="file"
        className="hidden"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          if (!picked) return;
          if (picked.size > 16 * 1024 * 1024) { onError(`${picked.name} is ${formatBytes(picked.size)}. WhatsApp accepts up to 16 MB.`); return; }
          upload.mutate(picked);
        }}
      />
      <span className="min-w-0 flex-1 text-small">
        {file ? (
          <>
            <span className="block truncate font-medium">{file.name}</span>
            <span className="block text-micro text-(--color-muted)">{file.size !== null ? `${formatBytes(file.size)} — ` : ''}sent with the text; the text becomes its caption</span>
          </>
        ) : (
          <span className="text-(--color-muted)">No file. Optional — a brochure, a price list, a product photo.</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={upload.isPending || channelId === null}
        title={channelId === null ? 'An active number is needed to upload' : undefined}
        className="tap shrink-0 rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small font-medium hover:bg-(--color-sunken) disabled:opacity-50"
      >
        {upload.isPending ? 'Uploading…' : file ? 'Replace' : 'Attach file'}
      </button>
      {file && (
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={upload.isPending}
          className="tap shrink-0 rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small text-(--color-muted) hover:text-(--color-bad) disabled:opacity-50"
        >
          Remove
        </button>
      )}
    </div>
  );
}

export type { CannedReply };
